import { useState, useCallback, useMemo } from 'react';
import api from '../config/api';

export const useSuggestions = (tripId, { usePartnerAuth = false } = {}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestConfig = useMemo(() => (usePartnerAuth ? { usePartnerAuth: true } : undefined), [usePartnerAuth]);

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/trips/${tripId}/suggestions`, requestConfig);
      setSuggestions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch suggestions');
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId, requestConfig]);

  const addSuggestion = useCallback(async (suggestionData) => {
    try {
      setError(null);
      const response = await api.post(`/trips/${tripId}/suggestions`, suggestionData, requestConfig);
      setSuggestions(prev => [response.data, ...prev]);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add suggestion');
      throw err;
    }
  }, [tripId, requestConfig]);

  const updateSuggestionStatus = useCallback(async (suggestionId, status, convertToItinerary = false) => {
    try {
      setError(null);
      const response = await api.put(`/trips/${tripId}/suggestions/${suggestionId}/status`, {
        status,
        convert_to_itinerary: convertToItinerary
      }, requestConfig);

      const nextStatus = convertToItinerary ? 'approved' : status;
      setSuggestions(prev => prev.map(suggestion =>
        suggestion.id === suggestionId
          ? { ...suggestion, status: nextStatus }
          : suggestion
      ));

      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update suggestion status');
      throw err;
    }
  }, [tripId, requestConfig]);

  const deleteSuggestion = useCallback(async (suggestionId) => {
    try {
      setError(null);
      await api.delete(`/trips/${tripId}/suggestions/${suggestionId}`, requestConfig);
      setSuggestions(prev => prev.filter(suggestion => suggestion.id !== suggestionId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete suggestion');
      throw err;
    }
  }, [tripId, requestConfig]);

  const addDiscussion = useCallback(async (suggestionId, message) => {
    try {
      setError(null);
      const response = await api.post(`/trips/${tripId}/suggestions/${suggestionId}/discussions`, { message }, requestConfig);
      setSuggestions(prev => prev.map(suggestion =>
        suggestion.id === suggestionId
          ? { ...suggestion, discussions: [...(suggestion.discussions || []), response.data] }
          : suggestion
      ));
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add discussion message');
      throw err;
    }
  }, [tripId, requestConfig]);

  const vote = useCallback(async (suggestionId, voteType) => {
    try {
      setError(null);
      // voteType is already in backend format ('up' or 'down')
      const response = await api.post(`/trips/${tripId}/suggestions/${suggestionId}/vote`, { vote: voteType }, requestConfig);

      setSuggestions(prev => prev.map(suggestion =>
        suggestion.id === suggestionId
          ? {
              ...suggestion,
              upvotes: response.data.upvotes,
              downvotes: response.data.downvotes,
              vote_score: response.data.vote_score,
              user_vote: response.data.user_vote
            }
          : suggestion
      ));

      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record vote');
      throw err;
    }
  }, [tripId, requestConfig]);

  const updateSuggestion = useCallback(async (suggestionId, suggestionData) => {
    try {
      setError(null);
      const response = await api.put(`/trips/${tripId}/suggestions/${suggestionId}`, suggestionData, requestConfig);
      setSuggestions(prev => prev.map(suggestion =>
        suggestion.id === suggestionId
          ? { ...suggestion, ...response.data }
          : suggestion
      ));
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update suggestion');
      throw err;
    }
  }, [tripId, requestConfig]);

  const deleteDiscussion = useCallback(async (suggestionId, discussionId) => {
    try {
      setError(null);
      await api.delete(`/trips/${tripId}/suggestions/${suggestionId}/discussions/${discussionId}`, requestConfig);
      setSuggestions(prev => prev.map(suggestion =>
        suggestion.id === suggestionId
          ? {
              ...suggestion,
              discussions: (suggestion.discussions || []).filter(discussion => discussion.id !== discussionId)
            }
          : suggestion
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete discussion message');
      throw err;
    }
  }, [tripId, requestConfig]);

  return {
    suggestions,
    loading,
    error,
    fetchSuggestions,
    addSuggestion,
    updateSuggestion,
    updateSuggestionStatus,
    deleteSuggestion,
    addDiscussion,
    deleteDiscussion,
    vote,
  };
};
